// This is the client's API it uses for interacting with the `server`.

import { AxiosError } from "axios";

import { type PageType, type SectionMonitoringData } from "../components/server-types";

import { SERVER_API_PATH } from "./api-constants";
import type {
  InviteStatus,
  PageAssessmentStatusType,
  ProgressOverviewData,
  SectionAssessmentSummaryParams,
  SystemCheckResultsData,
  UserEngagementDataType,
  UserInviteStatusType,
} from "./api-data-types";
import { serverApi } from "./api-server-actions";
import { createCached } from "./cache";

import {
  FullAssessmentData,
  PageAssessmentSummaryData,
  SectionAssessmentSummaryData,
  SectionEnrollmentData,
  SectionOutcomeSummaryData,
  StudentOutcomeCompetencyData,
} from "api/api-reporting-types";
import { reportApiFailure, withApiErrorReporting } from "utils/api-errors";
import { HomeRole } from "utils/urls";

export async function serverGetPageAssessmentStatuses({
  pageIds,
  sectionId,
}: {
  pageIds?: string[];
  sectionId?: string;
}): Promise<PageAssessmentStatusType> {
  const params: { page_ids?: string[]; section_id?: string } = {};
  if (pageIds) {
    params.page_ids = pageIds;
  }

  if (sectionId) {
    params.section_id = sectionId;
  }

  return withApiErrorReporting("serverPageAssessmentStatus", async () => {
    const response = await serverApi.post<PageAssessmentStatusType>(
      `${SERVER_API_PATH}/material/pages/assessment_status/`,
      params,
    );

    return response.data;
  });
}

// Type for the server response format
type UserInviteStatusResponseType = Array<{
  user_id: string;
  invite_status: string;
}>;

export async function serverGetUserInviteStatuses(): Promise<UserInviteStatusType> {
  return withApiErrorReporting("serverUserInviteStatus", async () => {
    const response = await serverApi.get<UserInviteStatusResponseType>(
      `${SERVER_API_PATH}/orgs/org_users/invite_status/`,
    );

    // Convert array of objects to Record<string, InviteStatus>
    const statusesRecord: UserInviteStatusType = {};
    response.data.forEach((item) => {
      statusesRecord[item.user_id] = item.invite_status as InviteStatus;
    });

    return statusesRecord;
  });
}

export async function serverGetSectionAssessmentSummary(
  params: SectionAssessmentSummaryParams,
): Promise<SectionAssessmentSummaryData[]> {
  return withApiErrorReporting("serverGetSectionAssessmentSummary", async () => {
    const response = await serverApi.post<SectionAssessmentSummaryData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/section_summary/`,
      params,
    );

    return response.data;
  });
}

export async function serverGetPageAssessmentSummary(
  params: SectionAssessmentSummaryParams,
): Promise<PageAssessmentSummaryData[]> {
  return withApiErrorReporting("serverGetPageAssessmentSummary", async () => {
    const response = await serverApi.post<PageAssessmentSummaryData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/page_summary/`,
      params,
    );

    return response.data;
  });
}

export async function serverGetFullAssessmentData(
  params: SectionAssessmentSummaryParams,
): Promise<FullAssessmentData[]> {
  return withApiErrorReporting("serverGetFullAssessmentData", async () => {
    const response = await serverApi.post<FullAssessmentData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/full_report/`,
      params,
    );

    return response.data;
  });
}

export async function serverGetSectionEnrollmentSummary(params: {
  term_id: string;
  course_id?: string;
  section_id?: string;
}): Promise<SectionEnrollmentData[]> {
  return withApiErrorReporting("serverGetSectionEnrollmentSummary", async () => {
    const response = await serverApi.post<SectionEnrollmentData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/section_enrollment_summary/`,
      params,
    );

    return response.data;
  });
}

export async function serverGetSectionOutcomeSummary(
  termId: string,
): Promise<SectionOutcomeSummaryData[]> {
  return withApiErrorReporting("serverGetSectionOutcomeSummary", async () => {
    const response = await serverApi.post<SectionOutcomeSummaryData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/section_outcome_summary/`,
      { term_id: termId },
    );

    return response.data;
  });
}

export async function serverGetStudentOutcomeCompetency(
  termId?: string,
): Promise<StudentOutcomeCompetencyData[]> {
  return withApiErrorReporting("serverGetStudentOutcomeCompetency", async () => {
    const response = await serverApi.post<StudentOutcomeCompetencyData[]>(
      `${SERVER_API_PATH}/material/assessment_summaries/student_outcome_competency/`,
      { term_id: termId },
    );

    return response.data;
  });
}

// Returns engagement data for all 3 date ranges: 7 days, 30 days, and -1 (all time)
export const serverGetEngagementData = createCached(
  async (sectionId: string): Promise<Record<number, Record<string, UserEngagementDataType>>> => {
    return withApiErrorReporting("serverGetEngagementData", async () => {
      const response = await serverApi.post<Record<number, Record<string, UserEngagementDataType>>>(
        `${SERVER_API_PATH}/material/sections/${sectionId}/engagement/`,
        {},
      );

      return response.data;
    });
  },
  60 * 10, // 10 minutes
);

export async function serverGetServerTime(): Promise<{ server_time: number }> {
  return withApiErrorReporting("serverGetServerTime", async () => {
    const response = await serverApi.get<{ server_time: number }>("/api/v1/basebox/server-time/", {
      headers: {
        "Cache-Control": "no-store",
        Pragma: "no-cache",
      },
    });
    return response.data;
  });
}

export async function serverGetProgressOverviewData(): Promise<ProgressOverviewData> {
  return withApiErrorReporting("serverGetProgressOverviewData", async () => {
    const response = await serverApi.get<ProgressOverviewData>(
      `${SERVER_API_PATH}/material/outcome_assessments/progress_overview/`,
    );

    return response.data;
  });
}

// This should only be used for the monitoring dashboard. It retrieves nested section data across all orgs.
export async function serverGetSectionsForMonitoring(): Promise<SectionMonitoringData[]> {
  return withApiErrorReporting("serverGetSectionsForMonitoring", async () => {
    const response = await serverApi.get<SectionMonitoringData[]>(
      `${SERVER_API_PATH}/material/sections/for_monitoring/`,
    );
    return response.data;
  });
}

// Intentionally not using withApiErrorReporting: 503 means Datadog is not configured
// (expected in some envs) and should return null without logging to Rollbar.
export async function serverGetDatadogEmbedUrl(
  sectionId: string,
): Promise<{ iframe_url: string } | null> {
  try {
    const response = await serverApi.get<{ iframe_url: string }>(
      `${SERVER_API_PATH}/datadog/embed_url/`,
      { params: { section_id: sectionId } },
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 503) {
      return null;
    }
    reportApiFailure("serverGetDatadogEmbedUrl", error);
    throw error;
  }
}

export async function serverGetHomePlannerPages(homeRole: HomeRole): Promise<PageType[]> {
  return withApiErrorReporting("serverGetHomePlannerPages", async () => {
    const response = await serverApi.get<PageType[]>(
      `${SERVER_API_PATH}/material/pages/home_planner/?home_role=${homeRole}`,
    );

    return response.data;
  });
}

export async function serverHasLinkedStudentContent(pageId: string): Promise<boolean> {
  return withApiErrorReporting("serverHasLinkedStudentContent", async () => {
    const response = await serverApi.get<{ has_linked_student_content: boolean }>(
      `${SERVER_API_PATH}/material/pages/${pageId}/has_linked_student_content/`,
    );
    return response.data.has_linked_student_content;
  });
}

export async function serverGetSystemCheckResults(
  start_date: string,
): Promise<SystemCheckResultsData[]> {
  return withApiErrorReporting("serverGetSystemCheckResults", async () => {
    const response = await serverApi.get<SystemCheckResultsData[]>(
      `${SERVER_API_PATH}/system_check/check_results/`,
      {
        params: { created_at__gte: start_date },
      },
    );
    return response.data;
  });
}
